<script setup>
import UserLayout from "@/Layouts/UserLayout/UserLayout.vue"
import { ref, computed } from "vue"
import { usePage, router } from "@inertiajs/vue3"

const props = defineProps({
    pairings: {
        type: Object,
        required: true
    },
    filters: {
        type: Object,
        default: () => ({})
    },
    stats: {
        type: Object,
        default: () => ({})
    }
})

const page = usePage()
const currencySymbol = computed(() => page.props.currencySymbol || '$')

const dateFrom = ref(props.filters.date_from || '')
const dateTo = ref(props.filters.date_to || '')

const formatCurrency = (value) => {
    if (value === null || value === undefined) return `${currencySymbol.value}0.00`
    const num = parseFloat(value) || 0
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(num)
}

const formatNumber = (value) => {
    if (value === null || value === undefined) return '0'
    return new Intl.NumberFormat('en-US').format(value)
}

const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
}

const applyFilters = () => {
    router.get('/user/binary/pairings', {
        date_from: dateFrom.value,
        date_to: dateTo.value,
        page: 1
    }, {
        preserveState: true
    })
}

const resetFilters = () => {
    dateFrom.value = ''
    dateTo.value = ''
    router.get('/user/binary/pairings', {}, {
        preserveState: true
    })
}

const exportPairings = () => {
    const params = new URLSearchParams({
        date_from: dateFrom.value || '',
        date_to: dateTo.value || ''
    })
    window.location.href = `/user/binary/pairings/export?${params.toString()}`
}
</script>

<template>
    <UserLayout :page-title="'Binary MLM'" :page-section="'Pairing History'">
        <div class="bp-page">

            <!-- Welcome strip -->
            <div class="bp-welcome anim-up" style="--d:0ms">
                <div class="bpw-left">
                    <div class="bpw-icon">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                        </svg>
                    </div>
                    <div class="bpw-text">
                        <span class="bpw-eye">Binary MLM</span>
                        <h1 class="bpw-title">Pairing History</h1>
                    </div>
                </div>
                <div class="bpw-chips">
                    <div class="bpw-chip bpw-chip--purple">
                        <span class="bpw-chip-lbl">Pairings</span>
                        <span class="bpw-chip-val">{{ formatNumber(stats.total_pairings) }}</span>
                    </div>
                    <div class="bpw-chip bpw-chip--green">
                        <span class="bpw-chip-lbl">Rewards</span>
                        <span class="bpw-chip-val">{{ currencySymbol }}{{ formatCurrency(stats.total_rewards) }}</span>
                    </div>
                    <button @click="exportPairings" class="bpw-export">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                        </svg>
                        Export
                    </button>
                </div>
            </div>

            <!-- Stat cards -->
            <div class="bp-stats anim-up" style="--d:60ms">
                <div class="bp-stat" style="--ac:167,93,237">
                    <div class="bp-stat-icon">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                    </div>
                    <div class="bp-stat-val">{{ formatNumber(stats.total_pairings) }}</div>
                    <div class="bp-stat-lbl">Total Pairings</div>
                </div>
                <div class="bp-stat" style="--ac:34,197,94">
                    <div class="bp-stat-icon">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                    </div>
                    <div class="bp-stat-val">{{ currencySymbol }}{{ formatCurrency(stats.total_rewards) }}</div>
                    <div class="bp-stat-lbl">Total Rewards</div>
                </div>
                <div class="bp-stat" style="--ac:251,191,36">
                    <div class="bp-stat-icon">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                    </div>
                    <div class="bp-stat-val">{{ formatNumber(stats.today_pairings) }}</div>
                    <div class="bp-stat-lbl">Today's Pairings</div>
                </div>
                <div class="bp-stat" style="--ac:59,130,246">
                    <div class="bp-stat-icon">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                        </svg>
                    </div>
                    <div class="bp-stat-val">{{ currencySymbol }}{{ formatCurrency(stats.avg_reward) }}</div>
                    <div class="bp-stat-lbl">Avg Reward</div>
                </div>
            </div>

            <!-- Filter card -->
            <div class="bp-card anim-up" style="--d:120ms">
                <div class="bp-filter">
                    <div class="bp-filter-field">
                        <label class="bp-filter-label">Date From</label>
                        <input v-model="dateFrom" type="date" class="bp-filter-input">
                    </div>
                    <div class="bp-filter-field">
                        <label class="bp-filter-label">Date To</label>
                        <input v-model="dateTo" type="date" class="bp-filter-input">
                    </div>
                    <div class="bp-filter-actions">
                        <button @click="applyFilters" class="bp-btn-apply">Apply</button>
                        <button @click="resetFilters" class="bp-btn-reset">Reset</button>
                    </div>
                </div>
            </div>

            <!-- Table / List card -->
            <div class="bp-card anim-up" style="--d:180ms">
                <div class="bp-card-head">
                    <h2 class="bp-card-title">Pairings</h2>
                    <span class="bp-card-count">{{ pairings.total || 0 }} total</span>
                </div>

                <!-- Desktop table -->
                <div class="bp-table-wrap">
                    <table class="bp-table">
                        <thead>
                            <tr>
                                <th>Left Volume</th>
                                <th>Right Volume</th>
                                <th>Weaker Leg</th>
                                <th>Pairs</th>
                                <th>Reward</th>
                                <th>Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="pairings.data.length === 0">
                                <td colspan="6" class="bp-empty">No pairings found</td>
                            </tr>
                            <tr v-for="pairing in pairings.data" :key="pairing.id">
                                <td>{{ formatNumber(pairing.left_volume) }}</td>
                                <td>{{ formatNumber(pairing.right_volume) }}</td>
                                <td>
                                    <span class="bp-leg-badge"
                                        :class="pairing.weaker_leg === 'left' ? 'bp-leg-badge--green' : 'bp-leg-badge--purple'">
                                        {{ pairing.weaker_leg }}
                                    </span>
                                </td>
                                <td>{{ formatNumber(pairing.pairs_count) }}</td>
                                <td class="bp-reward">+{{ currencySymbol }}{{ formatCurrency(pairing.reward_amount) }}</td>
                                <td class="bp-date-cell">{{ formatDate(pairing.created_at) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Mobile cards -->
                <div class="bp-mob-list">
                    <div v-if="pairings.data.length === 0" class="bp-empty">No pairings found</div>
                    <div v-for="pairing in pairings.data" :key="pairing.id" class="bp-mob-item">
                        <div class="bp-mob-top">
                            <span class="bp-leg-badge"
                                :class="pairing.weaker_leg === 'left' ? 'bp-leg-badge--green' : 'bp-leg-badge--purple'">
                                {{ pairing.weaker_leg }} weaker
                            </span>
                            <div class="bp-reward">+{{ currencySymbol }}{{ formatCurrency(pairing.reward_amount) }}</div>
                        </div>
                        <div class="bp-mob-grid">
                            <div class="bp-mob-cell">
                                <span class="bp-mob-lbl">Left Vol</span>
                                <span class="bp-mob-val">{{ formatNumber(pairing.left_volume) }}</span>
                            </div>
                            <div class="bp-mob-cell">
                                <span class="bp-mob-lbl">Right Vol</span>
                                <span class="bp-mob-val">{{ formatNumber(pairing.right_volume) }}</span>
                            </div>
                            <div class="bp-mob-cell">
                                <span class="bp-mob-lbl">Pairs</span>
                                <span class="bp-mob-val">{{ formatNumber(pairing.pairs_count) }}</span>
                            </div>
                            <div class="bp-mob-cell">
                                <span class="bp-mob-lbl">Date</span>
                                <span class="bp-mob-val bp-mob-date">{{ formatDate(pairing.created_at) }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Pagination -->
                <div v-if="pairings.links && pairings.links.length > 3" class="bp-pagination">
                    <div class="bp-pag-info">
                        Showing <strong>{{ pairings.from }}</strong>–<strong>{{ pairings.to }}</strong>
                        of <strong>{{ pairings.total }}</strong>
                    </div>
                    <div class="bp-pag-btns">
                        <component
                            v-for="(link, index) in pairings.links"
                            :key="index"
                            :is="link.url ? 'a' : 'span'"
                            :href="link.url"
                            v-html="link.label"
                            :class="[
                                'bp-page-btn',
                                link.active     ? 'bp-page-btn--active'   : '',
                                !link.url       ? 'bp-page-btn--disabled' : ''
                            ]"
                        />
                    </div>
                </div>
            </div>

        </div>
    </UserLayout>
</template>

<style scoped>
/* ── animations ─────────────────────────────────────────────────── */
@keyframes slideUp {
    from { opacity:0; transform:translateY(22px) }
    to   { opacity:1; transform:none }
}
.anim-up {
    animation: slideUp 0.45s cubic-bezier(0.22,1,0.36,1) both;
    animation-delay: var(--d,0ms);
}

/* ── page ───────────────────────────────────────────────────────── */
.bp-page {
    max-width: 1280px;
    margin: 0 auto;
    padding: 14px 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    position: relative;
    z-index: 10;
    width: 100%;
}
@media (min-width:640px)  { .bp-page { padding: 18px 18px; gap: 14px; } }
@media (min-width:1024px) { .bp-page { padding: 20px 24px; gap: 16px; } }

/* ── welcome strip ──────────────────────────────────────────────── */
.bp-welcome {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    padding: 12px 16px;
    border-radius: 13px;
    background: rgba(167,93,237,0.06);
    border: 1px solid rgba(167,93,237,0.16);
}
.bpw-left { display:flex; align-items:center; gap:10px; }
.bpw-icon {
    width:40px; height:40px; border-radius:11px;
    background:linear-gradient(135deg,rgba(124,58,237,0.3),rgba(167,93,237,0.2));
    border:1px solid rgba(167,93,237,0.28);
    display:flex; align-items:center; justify-content:center;
    flex-shrink:0; color:#a75ded;
    box-shadow:0 0 14px rgba(167,93,237,0.22);
}
.bpw-icon svg { width:18px; height:18px; }
.bpw-eye  { font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:0.1em; color:rgba(167,93,237,0.7); display:block; }
.bpw-title { font-size:15px; font-weight:900; color:#fff; letter-spacing:-0.02em; margin:0; }
.bpw-chips { display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
.bpw-chip {
    display:flex; flex-direction:column; align-items:center; gap:2px;
    padding:6px 12px; border-radius:9px;
    background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.07);
    min-width:58px;
}
.bpw-chip-lbl { font-size:8px; font-weight:700; color:#4b5563; text-transform:uppercase; letter-spacing:0.12em; }
.bpw-chip-val { font-size:13px; font-weight:900; color:#fff; }
.bpw-chip--purple .bpw-chip-val { color:#c084fc; }
.bpw-chip--green  .bpw-chip-val { color:#4ade80; }
.bpw-export {
    display:inline-flex; align-items:center; gap:5px;
    padding:6px 12px; border-radius:9px;
    background:rgba(167,93,237,0.2); border:1px solid rgba(167,93,237,0.4);
    color:#c084fc; font-size:12px; font-weight:700; cursor:pointer;
    transition:all .2s;
}
.bpw-export:hover { background:rgba(167,93,237,0.32); }
.bpw-export svg { width:14px; height:14px; }

/* ── stat cards ─────────────────────────────────────────────────── */
.bp-stats {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
}
@media (min-width:768px) { .bp-stats { grid-template-columns: repeat(4, 1fr); } }

.bp-stat {
    position: relative;
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(var(--ac),.22);
    border-radius: 14px;
    padding: 12px;
    overflow: hidden;
    transition: transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s;
    cursor: default;
}
.bp-stat:hover { transform:translateY(-2px); box-shadow:0 8px 24px rgba(var(--ac),0.18); }
.bp-stat-icon {
    width:32px; height:32px; border-radius:8px;
    background:rgba(var(--ac),.15); border:1px solid rgba(var(--ac),.28);
    display:flex; align-items:center; justify-content:center;
    margin-bottom:10px; color:rgb(var(--ac));
}
.bp-stat-icon svg { width:15px; height:15px; }
.bp-stat-val {
    font-size:18px; font-weight:900; color:#fff; letter-spacing:-0.03em;
    margin-bottom:3px; line-height:1.1; word-break:break-all;
}
.bp-stat-lbl { font-size:10px; font-weight:700; color:#4b5563; text-transform:uppercase; letter-spacing:0.06em; line-height:1.2; }
@media(min-width:640px) { .bp-stat { padding:14px; } .bp-stat-val { font-size:20px; } }

/* ── card base ──────────────────────────────────────────────────── */
.bp-card {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(167,93,237,0.13);
    border-radius: 16px;
    overflow: hidden;
    position: relative;
    transition: border-color 0.25s;
}
.bp-card::before {
    content:'';
    position:absolute; top:0; left:14%; right:14%; height:1px;
    background:linear-gradient(90deg,transparent,rgba(167,93,237,0.32),transparent);
}
.bp-card:hover { border-color:rgba(167,93,237,0.24); }

/* ── filter ─────────────────────────────────────────────────────── */
.bp-filter {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
    padding: 14px;
}
@media (min-width:640px) { .bp-filter { grid-template-columns: 1fr 1fr auto; align-items:end; } }
.bp-filter-label { display:block; font-size:10px; font-weight:700; color:rgba(255,255,255,0.45); margin-bottom:5px; text-transform:uppercase; letter-spacing:0.06em; }
.bp-filter-input {
    width:100%; padding:8px 12px; border-radius:9px;
    background:rgba(255,255,255,0.05); border:1px solid rgba(167,93,237,0.22);
    color:#fff; font-size:13px;
    transition:border-color .2s;
}
.bp-filter-input:focus { outline:none; border-color:rgba(167,93,237,0.55); }
.bp-filter-input::-webkit-calendar-picker-indicator { filter:invert(1) opacity(.5); }
.bp-filter-actions { display:flex; gap:6px; align-items:end; }
.bp-btn-apply {
    flex:1; padding:8px 16px; border-radius:9px;
    background:rgba(167,93,237,0.25); border:1px solid rgba(167,93,237,0.45);
    color:#c084fc; font-size:12px; font-weight:700; cursor:pointer;
    transition:all .2s;
}
.bp-btn-apply:hover { background:rgba(167,93,237,0.4); }
.bp-btn-reset {
    padding:8px 16px; border-radius:9px;
    background:rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.12);
    color:rgba(255,255,255,0.55); font-size:12px; font-weight:700; cursor:pointer;
    transition:all .2s;
}
.bp-btn-reset:hover { background:rgba(255,255,255,0.1); color:#fff; }

/* ── card head ──────────────────────────────────────────────────── */
.bp-card-head {
    display:flex; align-items:center; justify-content:space-between;
    padding:14px 14px 10px;
    border-bottom:1px solid rgba(255,255,255,0.06);
}
.bp-card-title { font-size:13px; font-weight:800; color:#fff; letter-spacing:-0.01em; }
.bp-card-count {
    font-size:10px; font-weight:700; color:rgba(167,93,237,0.85);
    background:rgba(167,93,237,0.12); border:1px solid rgba(167,93,237,0.25);
    padding:3px 8px; border-radius:9999px;
}

/* ── desktop table ──────────────────────────────────────────────── */
.bp-table-wrap { display:none; overflow-x:auto; }
@media (min-width:900px) { .bp-table-wrap { display:block; } }
.bp-table { width:100%; border-collapse:collapse; font-size:13px; }
.bp-table thead tr { background:rgba(255,255,255,0.03); }
.bp-table th {
    padding:10px 14px; text-align:left;
    font-size:10px; font-weight:700; text-transform:uppercase;
    letter-spacing:0.08em; color:rgba(255,255,255,0.38);
    white-space:nowrap;
}
.bp-table tbody tr {
    border-top:1px solid rgba(255,255,255,0.05);
    transition:background .15s;
}
.bp-table tbody tr:hover { background:rgba(167,93,237,0.06); }
.bp-table td { padding:10px 14px; color:rgba(255,255,255,0.8); white-space:nowrap; }
.bp-reward  { color:#4ade80; font-weight:700; }
.bp-date-cell { color:rgba(255,255,255,0.45); font-size:11px; }
.bp-empty   { text-align:center; padding:3rem 1rem; color:rgba(255,255,255,0.3); }

/* ── weaker leg badge ───────────────────────────────────────────── */
.bp-leg-badge {
    display:inline-flex; padding:3px 8px; border-radius:9999px;
    font-size:10px; font-weight:700; text-transform:capitalize;
}
.bp-leg-badge--green  { background:rgba(34,197,94,0.15);  border:1px solid rgba(34,197,94,0.3);  color:#4ade80; }
.bp-leg-badge--purple { background:rgba(168,85,247,0.15); border:1px solid rgba(168,85,247,0.3); color:#c084fc; }

/* ── mobile list ────────────────────────────────────────────────── */
.bp-mob-list { display:flex; flex-direction:column; gap:8px; padding:14px; }
@media (min-width:900px) { .bp-mob-list { display:none; } }
.bp-mob-item {
    background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.07);
    border-radius:12px; padding:12px;
}
.bp-mob-top {
    display:flex; align-items:center; justify-content:space-between;
    margin-bottom:10px;
}
.bp-mob-grid { display:grid; grid-template-columns:1fr 1fr; gap:6px 10px; }
.bp-mob-cell { display:flex; flex-direction:column; gap:2px; }
.bp-mob-lbl { font-size:9px; font-weight:700; color:rgba(255,255,255,0.4); text-transform:uppercase; letter-spacing:0.08em; }
.bp-mob-val { font-size:12px; font-weight:700; color:#fff; }
.bp-mob-date { font-size:10px; color:rgba(255,255,255,0.5); }

/* ── pagination ─────────────────────────────────────────────────── */
.bp-pagination {
    display:flex; align-items:center; justify-content:space-between;
    flex-wrap:wrap; gap:8px;
    padding:12px 14px;
    border-top:1px solid rgba(255,255,255,0.06);
    background:rgba(255,255,255,0.02);
}
.bp-pag-info { font-size:11px; color:rgba(255,255,255,0.45); }
.bp-pag-info strong { color:rgba(255,255,255,0.75); }
.bp-pag-btns { display:flex; flex-wrap:wrap; gap:4px; }
.bp-page-btn {
    display:inline-flex; align-items:center; justify-content:center;
    min-width:28px; height:28px; padding:0 6px;
    border-radius:8px;
    font-size:11px; font-weight:600;
    background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.1);
    color:rgba(255,255,255,0.55);
    text-decoration:none; cursor:pointer; transition:all .2s;
}
.bp-page-btn:not(.bp-page-btn--disabled):not(.bp-page-btn--active):hover {
    background:rgba(167,93,237,0.18); border-color:rgba(167,93,237,0.38); color:#c084fc;
}
.bp-page-btn--active {
    background:rgba(167,93,237,0.3); border-color:rgba(167,93,237,0.55); color:#fff;
}
.bp-page-btn--disabled { opacity:.35; cursor:not-allowed; }
</style>
